/**
 * Navigation panel — add/edit/reorder/re-parent menu items.
 */
import {useEffect, useState} from 'react';
import type {EditorNavItem, EditorNavigationPayload, EditorPage} from '../../lib/types';
import {NAV_TARGET_LABELS, NAV_LINK_KIND_LABELS} from '../../lib/ui-labels';

interface Props {
  initial: EditorNavigationPayload;
  pages: EditorPage[];
  onSave: (next: EditorNavigationPayload) => Promise<void>;
  onDirty: () => void;
  saving: boolean;
}

const EMPTY: EditorNavigationPayload = {location: 'primary', items: []};

function nid(): string {
  return 'n-' + Math.random().toString(36).slice(2, 9);
}

export default function NavigationPanel({initial, pages, onSave, onDirty, saving}: Props) {
  const [draft, setDraft] = useState<EditorNavigationPayload>(initial || EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(initial || EMPTY);
  }, [initial]);

  const setItems = (items: EditorNavItem[]) => {
    setDraft((d: EditorNavigationPayload) => ({...d, items}));
    onDirty();
  };

  const addItem = () => {
    setItems([
      ...draft.items,
      {
        id: nid(),
        label: '新菜单',
        url: '',
        parent_id: null,
        order: draft.items.length,
        target: '_self',
      },
    ]);
  };

  const updateItem = (i: number, patch: Partial<EditorNavItem>) => {
    setItems(draft.items.map((it: EditorNavItem, idx: number) => (idx === i ? {...it, ...patch} : it)));
  };

  const removeItem = (i: number) => {
    const removedId = draft.items[i].id;
    setItems(
      draft.items
        .filter((_: EditorNavItem, idx: number) => idx !== i)
        .map((it: EditorNavItem) => (it.parent_id === removedId ? {...it, parent_id: null} : it))
    );
  };

  const moveItem = (i: number, dir: -1 | 1) => {
    const items = [...draft.items];
    const target = i + dir;
    if (target < 0 || target >= items.length) return;
    [items[i], items[target]] = [items[target], items[i]];
    items.forEach((it: EditorNavItem, idx: number) => (it.order = idx));
    setItems(items);
  };

  const setLinkType = (i: number, kind: 'page' | 'url') => {
    if (kind === 'page') {
      updateItem(i, {url: null, page_slug: pages[0]?.slug ?? null});
    } else {
      updateItem(i, {url: '', page_slug: null});
    }
  };

  const handleSave = async () => {
    setError(null);
    try {
      await onSave(draft);
    } catch (e: any) {
      setError(e?.message || '保存失败');
    }
  };

  const sorted = [...draft.items].sort((a, b) => {
    if ((a.parent_id || null) === (b.parent_id || null)) return a.order - b.order;
    if (!a.parent_id) return -1;
    if (!b.parent_id) return 1;
    return a.order - b.order;
  });

  const parents = draft.items.filter((it) => !it.parent_id);

  return (
    <div className="nav-panel">
      <h2>导航菜单</h2>
      {error && <div className="editor-error">{error}</div>}

      {sorted.length === 0 && (
        <p className="empty-hint">还没有菜单项。点击「+ 添加菜单」开始。</p>
      )}

      <ol className="nav-list">
        {sorted.map((it) => {
          const realIdx = draft.items.findIndex((x) => x.id === it.id);
          const isChild = !!it.parent_id;
          return (
            <li key={it.id} className={`nav-item ${isChild ? 'child' : 'parent'}`}>
              <div className="nav-item-head">
                <strong>{it.label || '（无标题）'}</strong>
                <span className="nav-item-id">id: {it.id}</span>
                <button
                  className="icon-btn"
                  onClick={() => moveItem(realIdx, -1)}
                  type="button"
                  title="上移"
                >
                  ↑
                </button>
                <button
                  className="icon-btn"
                  onClick={() => moveItem(realIdx, 1)}
                  type="button"
                  title="下移"
                >
                  ↓
                </button>
                <button
                  className="icon-btn danger"
                  onClick={() => removeItem(realIdx)}
                  type="button"
                  title="删除"
                >
                  ×
                </button>
              </div>
              <div className="field-grid">
                <label className="field">
                  <span className="field-label">显示文字 *</span>
                  <input
                    type="text"
                    value={it.label}
                    onChange={(e) => updateItem(realIdx, {label: e.target.value})}
                  />
                </label>
                <label className="field">
                  <span className="field-label">上级菜单</span>
                  <select
                    value={it.parent_id || ''}
                    onChange={(e) => updateItem(realIdx, {parent_id: e.target.value || null})}
                  >
                    <option value="">（顶级）</option>
                    {parents
                      .filter((p) => p.id !== it.id)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="field">
                  <span className="field-label">链接类型</span>
                  <select
                    value={it.page_slug ? 'page' : 'url'}
                    onChange={(e) => setLinkType(realIdx, e.target.value as 'page' | 'url')}
                  >
                    {Object.entries(NAV_LINK_KIND_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </label>
                {it.page_slug ? (
                  <label className="field">
                    <span className="field-label">选择页面</span>
                    <select
                      value={it.page_slug}
                      onChange={(e) => updateItem(realIdx, {page_slug: e.target.value})}
                    >
                      {pages.map((p) => (
                        <option key={p.slug} value={p.slug}>
                          {p.title} (/{p.slug})
                        </option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <label className="field">
                    <span className="field-label">链接地址 *</span>
                    <input
                      type="url"
                      value={it.url || ''}
                      onChange={(e) => updateItem(realIdx, {url: e.target.value})}
                    />
                  </label>
                )}
                <label className="field">
                  <span className="field-label">打开方式</span>
                  <select
                    value={it.target}
                    onChange={(e) =>
                      updateItem(realIdx, {target: e.target.value as '_self' | '_blank'})
                    }
                  >
                    {Object.entries(NAV_TARGET_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </label>
              </div>
            </li>
          );
        })}
      </ol>

      <button className="btn-secondary" onClick={addItem} type="button">
        + 添加菜单
      </button>

      <div className="section-editor-actions">
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? '保存中…' : '保存导航菜单'}
        </button>
      </div>
    </div>
  );
}
